import './accessible-dropdown.styles.scss';
import React, { FC, useRef, useState } from 'react';
import { Dropdown } from '../ant-design/dropdown';

declare type OverlayFunc = () => React.ReactElement;

interface DropdownProps {
    /**
     * Array of menu items to be displayed in dropdown
     */
    items?: Array<Item>;
    /**
     * Array of menu items to be displayed in dropdown
     */
    overlay?: React.ReactElement | OverlayFunc;
    /**
     * Class name to be attached to dropdown container
     */
    overlayClassName?: string;
    /**
     * Overwrites logic to decide when dropdown menu is visible
     */
    visible?: boolean;
    /**
     * To set the container of the dropdown menu. The default is 
     * to create a div element in body, but you can reset it to 
     * the scrolling area and make a relative reposition.
     */
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    /**
     * Whether custom overlay should use pre-made overlay styles
     */
    useOverlayStyles?: boolean;
    /**
     * Used to differentiate dropdown from others by adding a
     * class to important components
     */
    uniqueIdentifier: String;
    /**
     * Placement of dropdown menu
     */
    placement?: 'bottomLeft' | 'bottomCenter' | 'bottomRight' | 'topLeft' | 'topCenter' | 'topRight';
    /**
     * Whether dropdown should have arrow
     */
    arrow?: boolean;
    /**
     * Whether the dropdown should toggle only on click
     * 
     * default: false 
     */
    clickedTriggeredOnly?: boolean
    /**
     * The element which triggers the dropdown
     *
     * If using a tab-able element such as <button> or <a>, make
     * sure to override the tabIndex to -1.
     */
    children?: any;
}

export interface Item {
    title?: string;
    href?: string;
    icon?: string;
    divider?: boolean;
    newTab?: boolean;
    toggleable?: boolean;
    disable?: boolean;
    /**
     * Renders a custom element instead of pre made anchor tag
     */
    custom?: any;
    /**
     * Adds attributes to item such as {"data-toggle": "value"}
     */
    attributes?: Object;
}

/**
 * A Simple Dropdown Component
 */
export const AccessibleDropdown: FC<DropdownProps> = React.memo(({
    items, overlay, useOverlayStyles, uniqueIdentifier, children, placement,
    arrow, clickedTriggeredOnly, overlayClassName, visible, getPopupContainer
}) => {
    const [shouldOpenDropdown, setShouldOpenDropdown] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);
    const firstDropdownItemRef = useRef<HTMLDivElement>(null);
    const dropdownActivationTriggers:("click" | "hover" | "contextMenu")[] = (!clickedTriggeredOnly) ? ['click', 'hover'] : ['click'];
    
    const delayTriggerDropdownOpen = () => {setTimeout(() => !shouldOpenDropdown && setShouldOpenDropdown(true), 500)};
    const triggerDropdownClose = () => {shouldOpenDropdown && setShouldOpenDropdown(false)};
    /**
     * 
     * @param e 
     */
    const triggerOnFocusOpen = (e:React.FocusEvent) =>{
        handleDropDownBlur(e, shouldOpenDropdown, triggerDropdownClose);
    }
    /**
     * Gets the focus of the first dropdown item
     */
    const focusFirstDropdownItem = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'ArrowDown') {
            if (firstDropdownItemRef.current) {
                firstDropdownItemRef.current.focus();
            }
        }
    };

    /**
     * Decides what to set as the overlay
     */
    const decideOverlay = () => {
        // Using custom overlay
        if (overlay) {
            if (useOverlayStyles) {
                return (
                    <DropdownOverlay
                        uniqueIdentifier={uniqueIdentifier}
                        placement={placement}
                        arrow={arrow}
                    >
                        {overlay}
                    </DropdownOverlay>
                );
            }
            return overlay;
        }
        // Using items to display dropdown
        if (items) {
            return <DropdownMenuView
                parentRef={parentRef}
                firstDropdownItemRef={firstDropdownItemRef}
                items={items}
                placement={placement}
                arrow={arrow}
                uniqueIdentifier={uniqueIdentifier}
            />;
        }
        return <span />;
    };

    // Render
    return (
        <Dropdown
            placement={placement}
            trigger={dropdownActivationTriggers}
            overlay={decideOverlay()}
            visible={visible !== undefined ? visible : shouldOpenDropdown}
            onVisibleChange={(v)=> setShouldOpenDropdown(!!v)}
            overlayClassName={overlayClassName}
            getPopupContainer={getPopupContainer ? getPopupContainer : undefined}
        >
            <span
                tabIndex={0}
                ref={parentRef}
                onFocus={delayTriggerDropdownOpen}
                onBlur={triggerOnFocusOpen}
                className={`tw-dropdown-parent ${uniqueIdentifier}`}
                onClick={e => e.preventDefault()}
                onKeyDown={e => focusFirstDropdownItem(e)}
            >
                {children}
            </span>
        </Dropdown>
    );
});


interface DropdownMenuViewProps extends DropdownProps {
    /**
     * Reference to button that triggered dropdown
     */
    parentRef: React.RefObject<HTMLDivElement>;
    /**
     * Reference to first item in dropdown menu
     */
    firstDropdownItemRef: React.RefObject<HTMLDivElement>;
}

/**
 * Parent iterates through items and controls whether to
 * render a clickable item or divider.
 */
const DropdownMenuView: FC<DropdownMenuViewProps> = React.memo((
    { items, parentRef, firstDropdownItemRef, uniqueIdentifier, placement, arrow }
) => (
        <DropdownOverlay
            uniqueIdentifier={uniqueIdentifier}
            placement={placement}
            arrow={arrow}
        >
            {items && items.map((item, i) => {
                if (item.divider) {
                    return <Divider uniqueIdentifier={uniqueIdentifier} />;
                }
                return (
                    <DropdownItem
                        key={`tw-drop-down-item-${uniqueIdentifier}-${i}`}
                        parentRef={parentRef}
                        firstDropdownItemRef={firstDropdownItemRef}
                        firstItem={i === 0}
                        uniqueIdentifier={uniqueIdentifier}
                        {...item}
                    />
                );
            })}
        </DropdownOverlay>
    ));

/**
 * Overlay styles for dropdown
 */
const DropdownOverlay: FC<DropdownProps> = React.memo((
    { uniqueIdentifier, placement, arrow, children }
) => (
        <div className={`
            tw-dropdown-menu
            ${uniqueIdentifier}
            ${arrow ? `tw-dropdown-menu-${placement ? placement : 'bottomLeft'} ` : ''}`
        }>
            {children}
        </div>
    )
);

interface DropdownItemProps extends Item, Omit<DropdownMenuViewProps, 'items, overlay'> {
    firstItem: boolean;
}

/**
 * Renders a child inside the dropdown which supports keyboard
 * accessibility with keyboard arrows.
 */
const DropdownItem: FC<DropdownItemProps> = (
    {
        href, newTab, icon, title, custom, attributes, toggleable,
        parentRef, firstDropdownItemRef, firstItem, uniqueIdentifier, disable
    }
) => {

    /**
     * Return null if dropdown item is disabled.
     */
    if(disable) {
        return null;
    }

    /**
     * Focuses parent
     */
    function focusParent() {
        if (parentRef.current) {
            parentRef.current.focus();
        }
    }

    /**
     * Recursive function that tries to focus previous sibling
     *
     * @return {boolean} Whether sibling was focused
     */
    function tryToFocusPreviousSibling(el: HTMLElement) {
        const previousSibling = el.previousSibling as HTMLElement;

        // Previous sibling exists
        if (el !== null && previousSibling !== null) {
            // Sibling is divider, ignore and check previous sibling.
            if (previousSibling.classList.contains('tw-dropdown-divider')) {
                tryToFocusPreviousSibling(previousSibling);
            }
            previousSibling.focus();
            return true;
        }
        return false;
    }

    /**
     * Recursive function that tries to focus next sibling
     *
     * @return {boolean} Whether sibling was focused
     */
    function tryToFocusNextSibling(el: HTMLElement) {
        const nextSibling = el.nextSibling as HTMLElement;

        // Next sibling exists
        if (el !== null && nextSibling !== null) {
            // Sibling is divider, ignore and check next sibling.
            if (nextSibling.classList.contains('tw-dropdown-divider')) {
                tryToFocusNextSibling(nextSibling);
            }
            nextSibling.focus();
            return true;
        }
        return false;
    }

    /**
     * Handles key presses to navigate inside the dropdown and
     * decides when to close the dropdown. Focusing the parent
     * will close the dropdown.
     */
    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        const element = e.target as HTMLElement;

        // Up arrow is pressed OR shift key and tab are pressed
        if (e.key === 'ArrowUp' || (e.shiftKey && e.key === 'Tab')) {
            e.preventDefault();
            if (!tryToFocusPreviousSibling(element)) {
                focusParent();
            }
            // Down arrow is pressed
        } else if (e.key === 'ArrowDown' || e.key === 'Tab') {
            e.preventDefault();
            if (!tryToFocusNextSibling(element) && e.key === 'Tab') {
                focusParent();
            }
            // Escape is pressed
        } else if (e.key === 'Escape') {
            focusParent();
        } else if (e.key === 'Enter') {
            toggleItem(element);
            // Getting child element and clicking it
            const child = element.firstChild as HTMLElement;
            if (child) {
                child.click();
            }
        }
    }

    /**
     * Adds active class to element and removes from
     * others.
     */
    function toggleItem(element: HTMLElement) {
        const currentActiveElement = document.querySelector(`.${uniqueIdentifier}.tw-dropdown-toggleable.tw-dropdown-active`);
        if (currentActiveElement) {
            currentActiveElement.classList.toggle('tw-dropdown-active');
        }
        element.classList.add('tw-dropdown-active');
    }

    return (
        <div
            ref={firstItem ? firstDropdownItemRef : null}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            onClick={e => toggleItem(e.currentTarget as HTMLElement)}
            className={`tw-dropdown-item tw-dropdown-item-handler ${uniqueIdentifier} ${toggleable ? 'tw-dropdown-toggleable' : ''}`}
        >
            {custom ? custom :
                <a href={href} target={newTab ? '_blank' : ''} tabIndex={-1} {...attributes} >
                    {typeof icon === 'object' && icon}
                    {typeof icon === 'string' && <i className='material-icons-round md-24 icon'>{icon}</i>}
                    {title}
                </a>
            }
        </div>
    );
};

/**	
 * Cleans up and closes dropdown when loosing focus	
 */	
function handleDropDownBlur(e:React.FocusEvent, shouldOpenDropdown:boolean, onBlurCB:() => void) {
    const blurredElement: HTMLElement | null = e.target as HTMLElement;
    // Timeout needed to get focused element after blur	
    setTimeout(() => {	
        const nextFocusedElement = document.activeElement;
        if (	
            blurredElement && nextFocusedElement &&	
            // Next focused element is not a dropdown item	
            !nextFocusedElement.classList.contains('tw-dropdown-item-handler') &&	
            // AND the drop down is not already open	
            (blurredElement.classList.contains('ant-dropdown-open') ||
            shouldOpenDropdown)
        ) {	
            onBlurCB();
        }	
    }, 100);	
}

interface DividerProps extends Pick<DropdownProps, 'uniqueIdentifier'> { }

/**
 * Divider for menu
 */
const Divider: FC<DividerProps> = ({ uniqueIdentifier }) => (
    <div className={`tw-dropdown-divider ${uniqueIdentifier}`} />
);
